import { values } from 'lodash';
import { State } from '../state';
import { History } from 'history';
import {
  CompetitionPledge, CompetitionPledgeCategory, CompetitionUserScore, Story, PledgeCompletions,
  CompetitionEntityPledge, Entities, UserMemberships, Entity, CompetitionScoreLevel
} from '../../userApi';
import { getPledgeParams } from '../../utils/routerHelper';
import { I18next } from '../../utils/i18nextShim';
import {
  isDateEligibleForPledge, isUserTypeEligibleForPledge,
  CompetitionPledges, IntroduceYourselfPledgeUUID, isCloseOutSurveyPledge,
  estimatePointsAvailable, getCompetitionUserScore
} from '../../utils/userApiHelper';
import logger from '../../utils/logger';
import { canBypassPledgeParticipantRestrictions, isCompetitionSuperadmin } from './user';

export const userTotalPledges = (state: State) =>
  values(state.userPledgeCompletions).reduce((previous: number, n: number) => previous + n, 0);

export const profileTotalPledges = (state: State) =>
  values(state.profilePledgeCompletions).reduce((previous: number, n: number) => previous + n, 0);

/** Onboarding order: Introduce Yourself first, then everything else. */
const onboardingSortRank = (pledge: CompetitionPledge): number => {
  if (pledge.uuid === IntroduceYourselfPledgeUUID) return 0;
  return 1;
};

export const MOST_POPULAR_PLEDGE_LIMIT = 10;
export const POPULAR_PLEDGE_LIMIT = 5;

export interface PledgePopularityBadges {
  mostPopular: { [pledgeId: number]: boolean };
  popular: { [pledgeId: number]: boolean };
}

export const getCompetitionPledgeCompletionTotals = (state: State): { [pledgeId: number]: number } =>
  state.competitionPledgeCompletionTotals || {};

export const getFeaturedPledgeUUIDs = (state: State): string[] => {
  const config = state.competition && state.competition.config;
  if (!config) {
    return [];
  }
  const listed = config.featuredPledgeUUIDs;
  if (listed && listed.length) {
    return listed;
  }
  if (config.featuredPledgeUUID) {
    return [config.featuredPledgeUUID];
  }
  return [];
};

export const isFeaturedPledge = (state: State, pledge: CompetitionPledge): boolean =>
  getFeaturedPledgeUUIDs(state).indexOf(pledge.uuid) >= 0;

const rankPledgesBySectionPopularity = (
  pledges: CompetitionPledge[],
  completionTotals: { [pledgeId: number]: number }
): CompetitionPledge[] =>
  [...pledges]
    .sort((a, b) => {
      const popDelta = (completionTotals[b.id] || 0) - (completionTotals[a.id] || 0);
      if (popDelta !== 0) {
        return popDelta;
      }
      return a.shortTitle.localeCompare(b.shortTitle);
    })
    .filter((pledge) => (completionTotals[pledge.id] || 0) > 0);

export const pledgePopularityBadgesInSection = (
  pledges: CompetitionPledge[],
  completionTotals: { [pledgeId: number]: number },
  mostPopularLimit = MOST_POPULAR_PLEDGE_LIMIT,
  popularLimit = POPULAR_PLEDGE_LIMIT
): PledgePopularityBadges => {
  const ranked = rankPledgesBySectionPopularity(pledges, completionTotals);
  const mostPopularSlice = ranked.slice(0, mostPopularLimit);
  const popularSlice = ranked.slice(mostPopularLimit, mostPopularLimit + popularLimit);
  const toMap = (list: CompetitionPledge[]) =>
    list.reduce((previous, pledge) => ({ ...previous, [pledge.id]: true }), {});
  return {
    mostPopular: toMap(mostPopularSlice),
    popular: toMap(popularSlice)
  };
};

/** @deprecated Use pledgePopularityBadgesInSection */
export const mostPopularPledgeIdsInSection = (
  pledges: CompetitionPledge[],
  completionTotals: { [pledgeId: number]: number },
  limit = MOST_POPULAR_PLEDGE_LIMIT
): { [pledgeId: number]: boolean } =>
  pledgePopularityBadgesInSection(pledges, completionTotals, limit).mostPopular;

const sortPledgesByRemainingPoints = (pledges: CompetitionPledge[], userPledgeCompletions: PledgeCompletions) =>
  pledges.sort((pledge1, pledge2) => {
    const rankDelta = onboardingSortRank(pledge1) - onboardingSortRank(pledge2);
    if (rankDelta !== 0) return rankDelta;
    return estimatePointsAvailable(userPledgeCompletions, pledge1) > estimatePointsAvailable(userPledgeCompletions, pledge2) ? -1 : 1;
  });

/** Score-level decks: onboarding first, featured pins, then competition popularity. */
export const sortPledgesForScoreDeck = (
  pledges: CompetitionPledge[],
  featuredUUIDs: string[],
  completionTotals: { [pledgeId: number]: number }
): CompetitionPledge[] =>
  [...pledges].sort((pledge1, pledge2) => {
    const onboardingDelta = onboardingSortRank(pledge1) - onboardingSortRank(pledge2);
    if (onboardingDelta !== 0) {
      return onboardingDelta;
    }
    const featuredIndex1 = featuredUUIDs.indexOf(pledge1.uuid);
    const featuredIndex2 = featuredUUIDs.indexOf(pledge2.uuid);
    const featured1 = featuredIndex1 >= 0;
    const featured2 = featuredIndex2 >= 0;
    if (featured1 && featured2) {
      return featuredIndex1 - featuredIndex2;
    }
    if (featured1) {
      return -1;
    }
    if (featured2) {
      return 1;
    }
    const popularityDelta = (completionTotals[pledge2.id] || 0) - (completionTotals[pledge1.id] || 0);
    if (popularityDelta !== 0) {
      return popularityDelta;
    }
    return pledge1.shortTitle.localeCompare(pledge2.shortTitle);
  });

/** Dashboard carousel: primary actions plus close-out survey and configured pin UUIDs. */
const filterPledgesForCarousel = (
  pledges: CompetitionPledge[],
  userPledgeCompletions: PledgeCompletions,
  pinUUIDs: string[]
): CompetitionPledge[] =>
  pledges.filter((pledge) => {
    if ((userPledgeCompletions[pledge.id] || 0) >= pledge.repeats) {
      return false;
    }
    if (pledge.uiLocation === CompetitionPledge.UiLocationEnum.Primary) {
      return true;
    }
    if (isCloseOutSurveyPledge(pledge)) {
      return true;
    }
    return pinUUIDs.indexOf(pledge.uuid) >= 0;
  });

const getCarouselPinUUIDs = (state: State): string[] => {
  const config = state.competition && state.competition.config;
  const suggested = (config && config.suggestedCarouselPledgeUUIDs) || [];
  const featured = getFeaturedPledgeUUIDs(state);
  const seen = new Set<string>();
  const ordered: string[] = [];
  [...suggested, ...featured].forEach((uuid) => {
    if (seen.has(uuid)) {
      return;
    }
    seen.add(uuid);
    ordered.push(uuid);
  });
  return ordered;
};

export const isIntroduceYourselfPledgeComplete = (state: State): boolean => {
  const pledge = findPledgeByUUID(state, { uuid: IntroduceYourselfPledgeUUID });
  if (!pledge || !state.userPledgeCompletions) {
    // No Introduce Yourself action in this catalog — treat as already done.
    return !pledge;
  }
  return (state.userPledgeCompletions[pledge.id] || 0) >= pledge.repeats;
};

export const isCalculatorPledgeComplete = (state: State): boolean => {
  const pledge = findPledgeByUUID(state, { uuid: CompetitionPledges.TakeCalculator });
  if (!pledge || !state.userPledgeCompletions) {
    return false;
  }
  return (state.userPledgeCompletions[pledge.id] || 0) >= pledge.repeats;
};

/** Introduce Yourself and end-of-challenge surveys are exempt from the intro gate. */
export const isUnlockedBeforeCalculatorComplete = (pledge: CompetitionPledge): boolean =>
  pledge.uuid === IntroduceYourselfPledgeUUID || isCloseOutSurveyPledge(pledge);

/** True if the user completed any action beyond Introduce Yourself / Calculator. */
export const hasCompletedPostOnboardingAction = (state: State): boolean => {
  const { competitionPledges, userPledgeCompletions } = state;
  if (!competitionPledges || !userPledgeCompletions) {
    return false;
  }
  return values(competitionPledges).some((pledge) => {
    if (!pledge) return false;
    if (pledge.uuid === IntroduceYourselfPledgeUUID) return false;
    if (pledge.uuid === CompetitionPledges.TakeCalculator) return false;
    return (userPledgeCompletions[pledge.id] || 0) > 0;
  });
};

/**
 * Onboarding gate: Introduce Yourself unlocks the rest (Calculator is optional).
 * Grandfather: users who already completed a post-onboarding action keep access
 * even without Introduce Yourself (they remain Novice).
 */
export const passesCalculatorGate = (state: State, pledge: CompetitionPledge): boolean => {
  if (canBypassPledgeParticipantRestrictions(state)) {
    return true;
  }
  if (isUnlockedBeforeCalculatorComplete(pledge)) {
    return true;
  }
  if (isIntroduceYourselfPledgeComplete(state)) {
    return true;
  }
  return hasCompletedPostOnboardingAction(state);
};

/** Score-level unlocking with Novice grandfather for pre-threshold earners. */
export const hasReachedScoreLevel = (
  state: State,
  scoreLevel: CompetitionScoreLevel | undefined,
  userPoints: number
): boolean => {
  if (canBypassPledgeParticipantRestrictions(state)) {
    return true;
  }
  if (!scoreLevel) {
    return true;
  }
  if (userPoints >= (scoreLevel.threshold || 0)) {
    return true;
  }
  // Users who already earned Novice action points before the threshold change keep Novice.
  if ((scoreLevel.name || '').toLowerCase() === 'novice') {
    return hasCompletedPostOnboardingAction(state);
  }
  return false;
};

/** Actions list/table: show locked score levels / gated actions grayed out (not hidden). */
export const isPledgeVisibleInActionsList = (state: State, pledge: CompetitionPledge): boolean => {
  const { user, competition } = state;
  if (!pledge || !user) {
    return false;
  }
  if (!isDateEligibleForPledge(pledge, competition)) {
    return false;
  }
  if (!isUserTypeEligibleForPledge(user, pledge)) {
    return false;
  }
  return true;
};

export const isPledgeAccessible = (state: State, pledge: CompetitionPledge): boolean => {
  if (!isUserEligibleForPledge(state, pledge)) {
    return false;
  }
  return passesCalculatorGate(state, pledge);
};

export const pinFeaturedPledgesFirst = (
  pledges: CompetitionPledge[],
  featuredUUIDs?: string[]
): CompetitionPledge[] => {
  if (!featuredUUIDs || !featuredUUIDs.length) {
    return pledges;
  }
  const featured = featuredUUIDs
    .map((uuid) => pledges.find((pledge) => pledge.uuid === uuid))
    .filter((pledge): pledge is CompetitionPledge => Boolean(pledge));
  if (!featured.length) {
    return pledges;
  }
  const featuredIDs = new Set(featured.map((pledge) => pledge.id));
  const remainder = pledges.filter((pledge) => !featuredIDs.has(pledge.id));
  return featured.concat(remainder);
};

/** @deprecated Use pinFeaturedPledgesFirst */
export const pinFeaturedPledgeFirst = (
  pledges: CompetitionPledge[],
  featuredUUID?: string
): CompetitionPledge[] => pinFeaturedPledgesFirst(pledges, featuredUUID ? [featuredUUID] : []);

export const carouselPledges = (state: State) => {
  const { competitionPledges, userPledgeCompletions } = state;
  const pledges = values(competitionPledges);
  const pinUUIDs = getCarouselPinUUIDs(state);

  const eligiblePledges = pledges.filter((pledge) => isPledgeAccessible(state, pledge));
  const filteredPledges = filterPledgesForCarousel(eligiblePledges, userPledgeCompletions, pinUUIDs);
  const sortedPledges = sortPledgesByRemainingPoints(filteredPledges, userPledgeCompletions);
  return pinFeaturedPledgesFirst(sortedPledges, pinUUIDs);
};

interface PledgeCategorical {
  competitionPledgeCategoryID: number;
}

interface PledgesByScoreLevel {
  scoreLevel?: CompetitionScoreLevel;
  pledges: CompetitionPledge[];
  active: boolean;
}

export const pledgesByCategoryAndLevel = (state: State, props: PledgeCategorical): PledgesByScoreLevel[] => {
  const { competitionPledges, competition, competitionUserScores, user, competitionScoreLevels, userPledgeCompletions } = state;
  if (!user || !competitionScoreLevels || !competitionScoreLevels.length || !competitionPledges) {
    return [];
  }
  const competitionUserScore = getCompetitionUserScore(competitionUserScores, user.id);
  const userPoints = competitionUserScore ? competitionUserScore.points : 0;
  // NOTE: Here we include pledges that user is NOT score level eligible for.
  const filteredPledges = values(competitionPledges).filter((pledge) => {
    if (!isDateEligibleForPledge(pledge, competition)) return false;
    if (!isUserTypeEligibleForPledge(user, pledge)) return false;
    if (!isPledgeVisibleInActionsList(state, pledge)) return false;
    if (props.competitionPledgeCategoryID === 0) return true;
    return pledge.competitionPledgeCategoryID === props.competitionPledgeCategoryID;
  });
  const featuredUUIDs = getFeaturedPledgeUUIDs(state);
  const completionTotals = getCompetitionPledgeCompletionTotals(state);
  const sortedPledges = sortPledgesForScoreDeck(filteredPledges, featuredUUIDs, completionTotals);
  return competitionScoreLevels
    .map((scoreLevel, i) => {
      if (i === 0) {
        return sortedPledges.filter((pledge) => !pledge.scoreLevelUUID || pledge.scoreLevelUUID === scoreLevel.uuid);
      } else {
        return sortedPledges.filter((pledge) => pledge.scoreLevelUUID === scoreLevel.uuid);
      }
    })
    .map((pledges, i) => ({
      scoreLevel: competitionScoreLevels[i],
      pledges,
      active: hasReachedScoreLevel(state, competitionScoreLevels[i], userPoints)
    }));
};

export const findPledgeByUUID = (state: State, { uuid }: { uuid: string }) =>
  state.competitionPledges[state.competitionPledgeIDsByUUID[uuid]];

interface Pledgeable {
  history: History;
  i18n: I18next;
}

export const isUserEligibleForStoryQueryPledge = (state: State) => {
  const { storyQuery, competitionPledges } = state;
  const { competitionPledgeID } = storyQuery;
  const pledge = competitionPledges[competitionPledgeID];
  if (!pledge) return false;
  return isUserEligibleForPledge(state, pledge);
};

export const isUserEligibleForPledge = (state: State, pledge: CompetitionPledge) => {
  try {
    const { user, competitionUserScores, competitionScoreLevels, competition } = state;
    if (!pledge || !user) return false;
    if (isCompetitionSuperadmin(state)) {
      return true;
    }
    if (canBypassPledgeParticipantRestrictions(state)) {
      return isDateEligibleForPledge(pledge, competition);
    }
    const score = getCompetitionUserScore(competitionUserScores, user.id);
    const userPoints = score ? score.points : 0;
    const scoreEligible = (() => {
      // Entry actions create the points used by later score levels, so their
      // catalog grouping must never lock a new participant out.
      if (isUnlockedBeforeCalculatorComplete(pledge)) return true;
      if (!pledge.scoreLevelUUID || !competitionScoreLevels) return true;
      const thresholdLevel = competitionScoreLevels.find((scoreLevel) => scoreLevel.uuid === pledge.scoreLevelUUID);
      if (!thresholdLevel) return true;
      return hasReachedScoreLevel(state, thresholdLevel, userPoints);
    })();
    return scoreEligible &&
      isUserTypeEligibleForPledge(user, pledge) &&
      isDateEligibleForPledge(pledge, competition);
  } catch (err) {
    logger.error(err);
    return true;
  }
};

export const currentPledge = (state: State, props: Pledgeable): CompetitionPledge =>
  state.competitionPledges[getPledgeParams(props.history.location, props.i18n).competitionPledgeID];

export const currentPledgeCategory = (state: State, props: Pledgeable): CompetitionPledgeCategory => {
  const pledge = state.competitionPledges[getPledgeParams(props.history.location, props.i18n).competitionPledgeID];
  return state.competitionPledgeCategories[pledge.competitionPledgeCategoryID];
};

export const lastUsersToVerify = (state: State, props: Pledgeable): CompetitionUserScore[] => {
  const pledgeID = getPledgeParams(props.history.location, props.i18n).competitionPledgeID;
  if (!state.latestToPledge || !state.latestToPledge[pledgeID]) return;
  return state.latestToPledge[pledgeID].map((userID) => state.competitionUserScores[userID]);
};

interface Storyable {
  story: Story;
}

export const userCompletionsLTPledgeRepeats = (state: State, props: Storyable): boolean => {
  if (!Boolean(props.story.competitionPledgeID) || !state.competitionPledges[props.story.competitionPledgeID] ) return;
  const completions = state.userPledgeCompletions[props.story.competitionPledgeID] || 0;
  const pledge = state.competitionPledges[props.story.competitionPledgeID];
  return completions < pledge.repeats;
};

interface CompetitionEntityPledgeable {
  entity: Entity;
  pledge: CompetitionPledge;
}

export const getCompetitionEntityPledge = (state: State, props: CompetitionEntityPledgeable): CompetitionEntityPledge => {
  const { competitionEntityPledges } = state;
  if (!competitionEntityPledges) return;

  const { entity, pledge } = props;
  return competitionEntityPledges[pledge.id][entity.id];
};

export const getEntitiesWithCompetitionEntityPledges = (state: State, props: Pledgeable): Partial<Entities> => {
  const { competitionEntityPledges, entities } = state;
  if (!competitionEntityPledges) return {};

  const { competitionPledgeID } = getPledgeParams(props.history.location, props.i18n);
  const entityIDs = Object.keys(competitionEntityPledges[competitionPledgeID] || {}) || [];
  return entityIDs.reduce((previous, entityID) => ({ [entityID]: entities[entityID], ...previous }), {});
};

export const getMembershipsCompetitionEntityPledges = (state: State, props: Pledgeable): Partial<UserMemberships> => {
  const { competitionEntityPledges, entities, userMemberships } = state;
  if (!competitionEntityPledges) return {};

  const { competitionPledgeID } = getPledgeParams(props.history.location, props.i18n);
  const entityIDs = Object.keys(competitionEntityPledges[competitionPledgeID] || {}) || [];
  return entityIDs.reduce((previous, entityID) => ({ [entityID]: userMemberships[entityID], ...previous }), {});
};

export const defaultCalculatorPoints = 500;

export const calculatorAvailablePoints = (state: State) => {
  if (!state.userPledgeCompletions) return defaultCalculatorPoints;
  const pledge = values(state.competitionPledges).find((p) => p.uuid === CompetitionPledges.TakeCalculator);
  if (!pledge) return defaultCalculatorPoints;
  return estimatePointsAvailable(state.userPledgeCompletions, pledge);
};
